import React, { useState, useEffect } from 'react';
import { Share2, RefreshCw, AlertCircle, AlertTriangle } from 'lucide-react';
import { TikTokRecipe } from '../../types/tiktok';
import { TikTokIngredientMatcher } from './TikTokIngredientMatcher';
import { TikTokRecipeEditor } from './TikTokRecipeEditor';
import { MatchedIngredient } from '../../types/ingredients';

type BackendStatus = 'checking' | 'online' | 'offline';
type AppStep = 'form' | 'matching' | 'editor';
type Platform = 'youtube' | 'tiktok' | 'instagram' | 'facebook' | 'unknown' | null;

const PLATFORM_CONFIG: Record<string, { label: string; bgClass: string }> = {
  youtube: { label: 'YouTube', bgClass: 'bg-red-100 text-red-800' },
  tiktok: { label: 'TikTok', bgClass: 'bg-gray-900 text-white' },
  instagram: { label: 'Instagram', bgClass: 'bg-purple-100 text-purple-800' },
  facebook: { label: 'Facebook', bgClass: 'bg-blue-100 text-blue-800' },
};

const LANGUAGES = [
  { code: '', label: 'Langue originale (pas de traduction)' },
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'Anglais' },
  { code: 'es', label: 'Espagnol' },
  { code: 'it', label: 'Italien' },
  { code: 'de', label: 'Allemand' },
];

function detectPlatform(url: string): Platform {
  if (!url) return null;
  const lower = url.toLowerCase();
  if (lower.includes('youtube.com') || lower.includes('youtu.be')) return 'youtube';
  if (lower.includes('tiktok.com')) return 'tiktok';
  if (lower.includes('instagram.com')) return 'instagram';
  if (lower.includes('facebook.com') || lower.includes('fb.watch')) return 'facebook';
  if (url.startsWith('http')) return 'unknown';
  return null;
}

export const SocialImportReader: React.FC = () => {
  const [url, setUrl] = useState('');
  const [targetLanguage, setTargetLanguage] = useState('');
  const [detectedPlatform, setDetectedPlatform] = useState<Platform>(null);
  const [recipe, setRecipe] = useState<TikTokRecipe | null>(null);
  const [finalRecipe, setFinalRecipe] = useState<TikTokRecipe | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [backendStatus, setBackendStatus] = useState<BackendStatus>('checking');
  const [currentStep, setCurrentStep] = useState<AppStep>('form');

  const backendUrl = import.meta.env.VITE_SOCIAL_IMPORT_BACKEND_URL || 'http://localhost:8000';

  useEffect(() => {
    checkBackend();
  }, []);

  useEffect(() => {
    setDetectedPlatform(detectPlatform(url));
  }, [url]);

  const checkBackend = async () => {
    setBackendStatus('checking');
    try {
      const response = await fetch(`${backendUrl}/health`);
      setBackendStatus(response.ok ? 'online' : 'offline');
    } catch {
      setBackendStatus('offline');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;

    setLoading(true);
    setError(null);
    setWarning(null);

    try {
      const body: { url: string; targetLanguage?: string } = { url: url.trim() };
      if (targetLanguage) body.targetLanguage = targetLanguage;

      const response = await fetch(`${backendUrl}/api/extract-recipe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || err.error || "Erreur lors de l'extraction");
      }

      const result = await response.json();
      if (result.warnings?.length) {
        setWarning(result.warnings[0]);
      }
      setRecipe(result.data.recipe);

      const hasMatches = result.data.recipe.ingredients.some(
        (ing: { standardIngredientId?: string }) => ing.standardIngredientId
      );

      if (targetLanguage && hasMatches) {
        setFinalRecipe(result.data.recipe);
        setCurrentStep('editor');
      } else {
        setCurrentStep('matching');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmMatches = (matches: MatchedIngredient[]) => {
    if (!recipe) return;
    const updatedRecipe: TikTokRecipe = {
      ...recipe,
      ingredients: matches.map((match) => ({
        id: match.extracted.id,
        name: match.matched?.name || match.extracted.name,
        amount: match.extracted.amount,
        unit: match.extracted.unit,
        standardIngredientId: match.matched?.id,
        category: match.matched?.category,
        matchedStandardName: match.matched?.name,
      })),
    };
    setFinalRecipe(updatedRecipe);
    setCurrentStep('editor');
  };

  const handleReset = () => {
    setRecipe(null);
    setFinalRecipe(null);
    setError(null);
    setWarning(null);
    setCurrentStep('form');
    setUrl('');
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Share2 className="h-6 w-6 text-menioo-green" />
              Social Import
            </h2>
            <p className="text-gray-600 mt-1">
              Importez des recettes depuis YouTube, TikTok, Instagram ou Facebook
            </p>
          </div>

          <div className="flex items-center gap-2">
            {backendStatus === 'online' && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                ✅ Backend connecté
              </span>
            )}
            {backendStatus === 'offline' && (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                  ❌ Backend hors ligne
                </span>
                <button
                  onClick={checkBackend}
                  className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
                  title="Réessayer"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              </div>
            )}
            {backendStatus === 'checking' && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                Vérification…
              </span>
            )}
          </div>
        </div>

        {/* Supported platforms badges */}
        <div className="flex flex-wrap gap-2 mb-6">
          {Object.entries(PLATFORM_CONFIG).map(([key, config]) => (
            <span
              key={key}
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.bgClass}`}
            >
              {config.label}
            </span>
          ))}
        </div>

        {/* Error banner */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Warning banner (non-blocking) */}
        {warning && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">{warning}</p>
          </div>
        )}

        {/* Step: Form */}
        {currentStep === 'form' && (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* URL input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                URL de la vidéo
              </label>
              <div className="relative">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=… ou https://tiktok.com/@user/video/…"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm pr-36 focus:border-menioo-green focus:ring-1 focus:ring-menioo-green disabled:bg-gray-50 disabled:text-gray-500"
                  required
                  disabled={loading || backendStatus === 'offline'}
                />
                {/* Platform badge inside input */}
                {detectedPlatform && detectedPlatform !== 'unknown' && PLATFORM_CONFIG[detectedPlatform] && (
                  <span
                    className={`absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PLATFORM_CONFIG[detectedPlatform].bgClass}`}
                  >
                    {PLATFORM_CONFIG[detectedPlatform].label}
                  </span>
                )}
                {detectedPlatform === 'unknown' && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                    Non supporté
                  </span>
                )}
              </div>
            </div>

            {/* Language selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Langue cible <span className="text-gray-400 font-normal">(optionnel)</span>
              </label>
              <select
                value={targetLanguage}
                onChange={(e) => setTargetLanguage(e.target.value)}
                className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-menioo-green focus:ring-1 focus:ring-menioo-green"
                disabled={loading}
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={
                loading ||
                backendStatus === 'offline' ||
                !url.trim() ||
                detectedPlatform === 'unknown'
              }
              className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-menioo-green hover:bg-menioo-green-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-menioo-green disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Extraction en cours…
                </>
              ) : (
                <>
                  <Share2 className="h-4 w-4" />
                  Extraire la recette
                </>
              )}
            </button>

            {backendStatus === 'offline' && (
              <p className="text-sm text-center text-gray-500">
                Backend Social Import non disponible.
                <br />
                Configurez{' '}
                <code className="bg-gray-100 px-1 rounded text-xs">
                  VITE_SOCIAL_IMPORT_BACKEND_URL
                </code>{' '}
                dans votre{' '}
                <code className="bg-gray-100 px-1 rounded text-xs">.env.local</code>.
              </p>
            )}
          </form>
        )}

        {/* Step: Ingredient matching */}
        {currentStep === 'matching' && recipe && (
          <TikTokIngredientMatcher
            recipe={recipe}
            onConfirm={handleConfirmMatches}
            onSkip={() => {
              setFinalRecipe(recipe);
              setCurrentStep('editor');
            }}
          />
        )}

        {/* Step: Recipe editor & save */}
        {currentStep === 'editor' && finalRecipe && (
          <TikTokRecipeEditor
            recipe={finalRecipe}
            onSuccess={() => {
              alert('✅ Recette sauvegardée avec succès !');
              handleReset();
            }}
            onCancel={() => setCurrentStep('matching')}
          />
        )}
      </div>
    </div>
  );
};
